import { validateProducts, validateFilters, validateSearchMeta, validateSortOptions } from '../utils/validation';
export function mapProducts(products) {
    const mappedProducts = products.map(product => {
        var _a;
        // Extract color and size from variants
        const colors = new Set();
        const sizes = new Set();
        product.variants.edges.forEach(({ node }) => {
            node.selectedOptions.forEach(option => {
                if (option.name.toLowerCase() === 'color') {
                    colors.add(option.value);
                }
                else if (option.name.toLowerCase() === 'size') {
                    sizes.add(option.value);
                }
            });
        });
        // Get first available color or default
        const color = Array.from(colors)[0] || '';
        return {
            id: product.id.split('/').pop() || product.id,
            title: product.title,
            handle: product.handle,
            brand: product.vendor,
            color,
            size: Array.from(sizes),
            price: {
                regular: parseFloat(product.priceRange.maxVariantPrice.amount),
                sale: product.priceRange.minVariantPrice.amount !== product.priceRange.maxVariantPrice.amount
                    ? parseFloat(product.priceRange.minVariantPrice.amount)
                    : undefined
            },
            image: product.images.edges[0] ? {
                url: product.images.edges[0].node.url,
                alt: product.images.edges[0].node.altText || product.title
            } : {
                url: '/placeholder.jpg',
                alt: product.title
            },
            availability: product.availableForSale ? 'in_stock' : 'out_of_stock',
            promoMessage: (_a = product.tags.find(tag => tag.startsWith('promo:'))) === null || _a === void 0 ? void 0 : _a.replace('promo:', '')
        };
    });
    return validateProducts(mappedProducts, 'Shopify');
}
export function mapFilters(productsOrFilters, currentFilters) {
    // Check if we received products array (from search) or filters array (from collection)
    if (!productsOrFilters || productsOrFilters.length === 0) {
        return validateFilters(getDefaultFilters(currentFilters), 'Shopify');
    }
    // If first item has product properties, generate filters from products
    const firstItem = productsOrFilters[0];
    if ('handle' in firstItem && 'vendor' in firstItem) {
        // This is a products array, generate filters from it
        const products = productsOrFilters;
        return validateFilters(generateFiltersFromProducts(products, currentFilters), 'Shopify');
    }
    // Otherwise it's a filters array
    const filters = productsOrFilters;
    const mappedFilters = filters.map(filter => {
        var _a;
        if (filter.type === 'PRICE_RANGE') {
            const currentPriceFilter = (_a = currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters.price) === null || _a === void 0 ? void 0 : _a[0];
            let currentMin = 0;
            let currentMax = 1000;
            if (currentPriceFilter) {
                const [min, max] = currentPriceFilter.split(',').map(Number);
                currentMin = min;
                currentMax = max;
            }
            return {
                id: 'price',
                label: 'Price',
                type: 'range',
                min: 0,
                max: 1000,
                gap: 50,
                currentMin,
                currentMax
            };
        }
        return {
            id: filter.id,
            label: filter.label,
            type: 'select',
            options: filter.values.map(value => {
                var _a;
                return ({
                    id: value.id,
                    label: value.label,
                    count: value.count,
                    selected: ((_a = currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters[filter.id]) === null || _a === void 0 ? void 0 : _a.includes(value.id)) || false,
                    value: value.id
                });
            })
        };
    });
    return validateFilters(mappedFilters, 'Shopify');
}
function getDefaultFilters(currentFilters) {
    var _a;
    const currentPriceFilter = (_a = currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters.price) === null || _a === void 0 ? void 0 : _a[0];
    let currentMin = 0;
    let currentMax = 1000;
    if (currentPriceFilter) {
        const [min, max] = currentPriceFilter.split(',').map(Number);
        currentMin = min;
        currentMax = max;
    }
    return [
        {
            id: 'vendor',
            label: 'Brand',
            type: 'select',
            options: []
        },
        {
            id: 'productType',
            label: 'Category',
            type: 'select',
            options: []
        },
        {
            id: 'price',
            label: 'Price',
            type: 'range',
            min: 0,
            max: 1000,
            gap: 50,
            currentMin,
            currentMax
        }
    ];
}
function generateFiltersFromProducts(products, currentFilters) {
    var _a;
    // Extract unique vendors and product types
    const vendors = new Map();
    const productTypes = new Map();
    let minPrice = Infinity;
    let maxPrice = 0;
    products.forEach(product => {
        // Count vendors
        if (product.vendor) {
            vendors.set(product.vendor, (vendors.get(product.vendor) || 0) + 1);
        }
        // Count product types
        if (product.productType) {
            productTypes.set(product.productType, (productTypes.get(product.productType) || 0) + 1);
        }
        // Track price range
        const price = parseFloat(product.priceRange.minVariantPrice.amount);
        if (price < minPrice)
            minPrice = price;
        if (price > maxPrice)
            maxPrice = price;
    });
    // Round price range
    minPrice = Math.floor(minPrice / 10) * 10;
    maxPrice = Math.ceil(maxPrice / 10) * 10;
    const currentPriceFilter = (_a = currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters.price) === null || _a === void 0 ? void 0 : _a[0];
    let currentMin = minPrice;
    let currentMax = maxPrice;
    if (currentPriceFilter) {
        const [min, max] = currentPriceFilter.split(',').map(Number);
        currentMin = min;
        currentMax = max;
    }
    return [
        {
            id: 'vendor',
            label: 'Brand',
            type: 'select',
            options: Array.from(vendors.entries()).map(([vendor, count]) => {
                var _a;
                return ({
                    id: vendor,
                    label: vendor,
                    count,
                    selected: ((_a = currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters.vendor) === null || _a === void 0 ? void 0 : _a.includes(vendor)) || false,
                    value: vendor
                });
            })
        },
        {
            id: 'productType',
            label: 'Category',
            type: 'select',
            options: Array.from(productTypes.entries()).map(([type, count]) => {
                var _a;
                return ({
                    id: type,
                    label: type,
                    count,
                    selected: ((_a = currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters.productType) === null || _a === void 0 ? void 0 : _a.includes(type)) || false,
                    value: type
                });
            })
        },
        {
            id: 'price',
            label: 'Price',
            type: 'range',
            min: minPrice,
            max: maxPrice,
            gap: 10,
            currentMin,
            currentMax
        }
    ];
}
export function mapMeta(response, currentPage, pageSize) {
    var _a;
    const products = response.data.products || ((_a = response.data.collection) === null || _a === void 0 ? void 0 : _a.products);
    if (!products) {
        return validateSearchMeta({
            totalResults: 0,
            totalPages: 0,
            currentPage: 1,
            pageSize
        }, 'Shopify');
    }
    // Shopify doesn't provide total count in GraphQL API
    // We estimate based on hasNextPage
    const hasMore = products.pageInfo.hasNextPage;
    const estimatedTotal = hasMore ? (currentPage + 1) * pageSize + 1 : currentPage * pageSize;
    return validateSearchMeta({
        totalResults: estimatedTotal,
        totalPages: Math.ceil(estimatedTotal / pageSize),
        currentPage,
        pageSize
    }, 'Shopify');
}
export function mapSorts() {
    return validateSortOptions([
        { id: 'relevance', label: 'Relevance' },
        { id: 'best-selling', label: 'Best Selling' },
        { id: 'price:asc', label: 'Price: Low to High' },
        { id: 'price:desc', label: 'Price: High to Low' },
        { id: 'title:asc', label: 'Title: A to Z' },
        { id: 'title:desc', label: 'Title: Z to A' },
        { id: 'created:desc', label: 'Newest First' }
    ], 'Shopify');
}
